import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Form, Input, Button, Card, Alert, Row, Col, message, Modal } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { authAPI } from '../api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './Login.css';

const LOGIN_TURNSTILE_SITE_KEY = process.env.REACT_APP_LOGIN_TURNSTILE_SITE_KEY;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || process.env.GOOGLE_CLIENT_ID;

const Login = ({ onLogin }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileModalVisible, setTurnstileModalVisible] = useState(false);
  const [pendingLoginValues, setPendingLoginValues] = useState(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleBindLoading, setGoogleBindLoading] = useState(false);
  const [googleBindModalVisible, setGoogleBindModalVisible] = useState(false);
  const [googleBindPassword, setGoogleBindPassword] = useState('');
  const [googleBindEmail, setGoogleBindEmail] = useState('');
  const [googleIdToken, setGoogleIdToken] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const originalTitle = document.title;
    document.title = `Login - ${originalTitle}`;

    return () => {
      document.title = originalTitle;
    };
  }, []);

  const redirectPath = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const redirect = params.get('redirect');
    if (redirect && redirect.startsWith('/')) {
      return redirect;
    }
    return '';
  }, [location.search]);

  const completeLoginFlow = useCallback(async (accessToken) => {
    localStorage.setItem('token', accessToken);

    const userResponse = await authAPI.getProfile();
    const userInfo = userResponse.data;

    const loginSuccess = await onLogin(accessToken);
    if (!loginSuccess) {
      setError('Failed to update authentication state');
      return false;
    }

    const pendingActionStr = localStorage.getItem('pending_action');
    if (pendingActionStr) {
      try {
        const pendingAction = JSON.parse(pendingActionStr);
        localStorage.removeItem('pending_action');

        if (userInfo.role === 'dropshipper' && pendingAction.action === 'order_samples') {
          const productData = {
            product_id: pendingAction.product_id,
            product_name: pendingAction.product_name,
            variant_path: pendingAction.variant_path,
            variant_data: pendingAction.variant_data,
            quantity: 1
          };
          localStorage.setItem('selected_product_for_order', JSON.stringify(productData));
          navigate(pendingAction.redirect_url);
          return true;
        }
      } catch (error) {
        localStorage.removeItem('pending_action');
      }
    }

    if (redirectPath) {
      navigate(redirectPath);
      return true;
    }

    if (userInfo.role === 'admin') {
      navigate('/admin');
    } else if (userInfo.role === 'dropshipper') {
      navigate('/dropshipper');
    } else {
      navigate('/dashboard');
    }
    return true;
  }, [navigate, onLogin, redirectPath]);

  const handleGoogleCredentialResponse = useCallback(async (response) => {
    if (!response?.credential) {
      return;
    }
    setGoogleLoading(true);
    setError('');

    try {
      const res = await authAPI.googleLogin({ id_token: response.credential });
      const data = res.data;

      if (data.requires_password) {
        setGoogleBindEmail(data.email || '');
        setGoogleIdToken(response.credential);
        setGoogleBindPassword('');
        setGoogleBindModalVisible(true);
        return;
      }

      if (data.access_token) {
        await completeLoginFlow(data.access_token);
      } else {
        setError('Google sign-in failed: missing access token');
      }
    } catch (err) {
      if (err.response) {
        const detail = err.response.data?.detail;
        setError(detail || 'Google sign-in failed');
      } else if (err.request) {
        setError('Network error, please try again later');
      } else {
        setError(`Google sign-in error: ${err.message}`);
      }
    } finally {
      setGoogleLoading(false);
    }
  }, [completeLoginFlow]);

  const initializeGoogleButton = useCallback(() => {
    if (!GOOGLE_CLIENT_ID || !window.google?.accounts?.id) {
      return;
    }
    const container = document.getElementById('google-login-button');
    if (!container) {
      return;
    }
    container.innerHTML = '';

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleGoogleCredentialResponse,
    });
    window.google.accounts.id.renderButton(container, {
      theme: 'outline',
      size: 'large',
      shape: 'rectangular',
      text: 'continue_with',
      width: 320,
    });
  }, [handleGoogleCredentialResponse]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      console.warn('GOOGLE_CLIENT_ID not set, Google login is disabled');
      return;
    }

    const handleScriptLoad = () => initializeGoogleButton();
    const existingScript = document.getElementById('google-identity-script');

    if (existingScript) {
      if (window.google?.accounts?.id) {
        initializeGoogleButton();
      } else {
        existingScript.addEventListener('load', handleScriptLoad);
      }

      return () => {
        existingScript.removeEventListener('load', handleScriptLoad);
      };
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.id = 'google-identity-script';
    script.onload = handleScriptLoad;
    document.head.appendChild(script);

    return () => {
      script.onload = null;
    };
  }, [initializeGoogleButton]);

  // 加载Cloudflare Turnstile脚本
  useEffect(() => {
    if (!LOGIN_TURNSTILE_SITE_KEY) {
      console.warn('LOGIN_TURNSTILE_SITE_KEY not set');
      return;
    }

    // 检查脚本是否已加载
    if (window.turnstile) {
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    return () => {
      // 清理脚本
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  // 显示Turnstile验证模态框
  const showTurnstileModal = (loginValues) => {
    setTurnstileModalVisible(true);
    setTurnstileToken(null);

    // 等待脚本加载完成后渲染Turnstile
    setTimeout(() => {
      if (window.turnstile && LOGIN_TURNSTILE_SITE_KEY) {
        // 先清理可能存在的旧widget
        const container = document.getElementById('login-turnstile-container');
        if (container) {
          container.innerHTML = '';
        }

        window.turnstile.render('#login-turnstile-container', {
          sitekey: LOGIN_TURNSTILE_SITE_KEY,
          callback: (token) => {
            setTurnstileToken(token);
            // 验证成功后自动执行登录
            setTurnstileModalVisible(false);
            performLogin(loginValues, token);
          },
          'error-callback': () => {
            message.error('Verification failed. Please try again.');
          },
          'expired-callback': () => {
            setTurnstileToken(null);
            message.warning('Verification expired. Please try again.');
          }
        });
      } else {
        message.error('Turnstile not loaded. Please refresh the page.');
        setTurnstileModalVisible(false);
      }
    }, 100);
  };

  // 处理模态框关闭
  const handleModalCancel = () => {
    // 清理Turnstile widget
    const container = document.getElementById('login-turnstile-container');
    if (container) {
      container.innerHTML = '';
    }

    setTurnstileModalVisible(false);
    setTurnstileToken(null);
    setPendingLoginValues(null);
  };

  // 实际执行登录
  const performLogin = async (values, token) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await authAPI.login({
        ...values,
        turnstile_token: token
      });
      const { access_token } = response.data;
      await completeLoginFlow(access_token);
    } catch (err) {
      
      
      if (err.response) {
        // 服务器返回了错误响应
        const status = err.response.status;
        const detail = err.response.data?.detail;
        
        if (status === 401 && detail === 'Incorrect username or password') {
          setError('Incorrect username or password');
        } else if (status === 400 && detail === 'Invalid turnstile token') {
          setError('Verification failed. Please complete the security check.');
          setTurnstileToken(null); // 重置token，让用户重新验证
        } else if (status === 401) {
          setError('Authentication failed');
        } else {
          setError(`Server error (${status}): ${detail || 'Unknown error'}`);
        }
      } else if (err.request) {
        // 请求已发出但没有收到响应
        setError('Unable to connect to server. Please check your network connection.');
      } else {
        // 其他错误
        setError(`Error: ${err.message}`);
      }
    } finally {
      setLoading(false);
      setPendingLoginValues(null);
    }
  };

  const onFinish = async (values) => {
    // 在开发环境下或未配置Turnstile时，直接登录
    if (process.env.NODE_ENV === 'development' || !LOGIN_TURNSTILE_SITE_KEY) {
      performLogin(values, '');
      return;
    }

    // 生产环境且配置了Turnstile，显示验证模态框
    setPendingLoginValues(values);
    showTurnstileModal(values);
  };

  const handleGoogleBind = async () => {
    if (!googleIdToken) {
      setError('Missing Google credential, please try again');
      setGoogleBindModalVisible(false);
      return;
    }
    if (!googleBindPassword) {
      message.error('Please enter your password to link this account');
      return;
    }

    setGoogleBindLoading(true);
    setError('');
    try {
      const res = await authAPI.googleLogin({
        id_token: googleIdToken,
        password: googleBindPassword,
      });
      const data = res.data;

      if (data.access_token) {
        setGoogleBindModalVisible(false);
        setGoogleBindPassword('');
        setGoogleIdToken('');
        await completeLoginFlow(data.access_token);
      } else {
        setError('Linking failed, please try again');
      }
    } catch (err) {
      if (err.response) {
        const detail = err.response.data?.detail;
        setError(detail || 'Linking failed, please check your password');
      } else if (err.request) {
        setError('Network error, please try again later');
      } else {
        setError(`Linking failed: ${err.message}`);
      }
    } finally {
      setGoogleBindLoading(false);
    }
  };

  return (
    <div className="login-page shared-background">
      {error && (
        <div
          style={{
            position: 'fixed',
            top: 16,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            zIndex: 2000
          }}
        >
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ maxWidth: 480, width: '90%' }}
          />
        </div>
      )}
      <div className="login-header-wrapper">
        <Header />
      </div>

      <div className="login-content">
        <Row justify="center" style={{ width: '100%' }}>
        <Col xs={20} sm={16} md={12} lg={8} xl={6}>
          <Card 
            title={
              <div style={{ textAlign: 'center', fontSize: '24px', fontWeight: 'bold' }}>
                Login
              </div>
            }
            style={{ 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              borderRadius: '10px',
              background: 'rgba(255, 255, 255, 0.5)'
            }}
          >
            <Form
            name="login"
            onFinish={onFinish}
            size="large"
            layout="vertical"
          >
              {GOOGLE_CLIENT_ID && (
                <div style={{ marginBottom: 16 }}>
                  <div
                    id="google-login-button"
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      pointerEvents: googleLoading ? 'none' : 'auto',
                      opacity: googleLoading ? 0.6 : 1
                    }}
                  ></div>
                </div>
              )}

              <Form.Item
                label="Username"
                name="username"
                required={false}
                rules={[{ required: true, message: 'Please enter username!' }]}
              >
                <Input 
                  prefix={<UserOutlined />} 
                  placeholder="Enter username" 
                />
              </Form.Item><Form.Item
                label="Password"
                name="password"
                required={false}
                rules={[{ required: true, message: 'Please enter password!' }]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Enter password"
                />
              </Form.Item>

              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={loading}
                  style={{ width: '100%', height: '40px' }}
                >
                  Login
                </Button>
              </Form.Item>

              <div style={{ textAlign: 'center' }}>
                Don't have an account?{' '}
                <Button type="link" onClick={() => navigate('/register')} style={{ padding: 0 }}>
                  Register here
                </Button>
              </div>
            </Form>
          </Card>
        </Col>
      </Row>
      </div>

      <Modal
        title="Link existing account"
        open={googleBindModalVisible}
        onCancel={() => {
          setGoogleBindModalVisible(false);
          setGoogleBindPassword('');
          setGoogleIdToken('');
        }}
        onOk={handleGoogleBind}
        okText="Link and sign in"
        cancelText="Cancel"
        confirmLoading={googleBindLoading}
        destroyOnClose
      >
        <p style={{ marginBottom: 12 }}>
          We found the email {googleBindEmail || 'this email'} in our system. Enter your password to link it.
        </p>
        <Input.Password
          prefix={<LockOutlined />}
          placeholder="Enter password to link"
          value={googleBindPassword}
          onChange={(e) => setGoogleBindPassword(e.target.value)}
          onPressEnter={handleGoogleBind}
        />
      </Modal>

      {/* Turnstile验证模态框 */}
      <Modal
        title="Security Check"
        open={turnstileModalVisible}
        footer={null}
        closable={true}
        centered
        width={400}
        onCancel={handleModalCancel}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div id="login-turnstile-container"></div>
          <div style={{ marginTop: '16px', color: '#666', fontSize: '14px' }}>
            Please Wait...
          </div>
        </div>
      </Modal>

      <div className="login-footer-wrapper">
        <Footer />
      </div>
    </div>
  );
};

export default Login;
